import { useCallback, useContext, useRef, useState } from "react";
import { AuthContext } from "./context/AuthContext";
import api from "./api/client";
import useAudioRecorder from "./hooks/useAudioRecorder";
import useQrScanner from "./hooks/useQrScanner";
import useAudioUrl from "./hooks/useAudioUrl";
import Login from "./Login";
import CustomAlert from "./CustomAlert";
import RecordingControls from "./components/RecordingControls";
import VehicleFields, { VinInput } from "./components/VehicleFields";
import QrControls from "./components/QrControls";
import PredictionPanel from "./components/PredictionPanel";
import Icon from "./components/Icon";
import "./App.css";

export default function App() {
  const { token, logout } = useContext(AuthContext);
  const [status, setStatus] = useState("");
  const [qrResult, setQrResult] = useState("");
  const [vin, setVin] = useState("");
  const [location, setLocation] = useState("Iran Khodro");
  const [milage, setMilage] = useState(0);
  const [description, setDescription] = useState("");
  const [label, setLabel] = useState(false);
  const [inferenceResult, setInferenceResult] = useState(null);
  const [experimentalResult, setExperimentalResult] = useState(null);
  const [modelLabels, setModelLabels] = useState(null);
  const [predictionAudio, setPredictionAudio] = useState(null);
  const [predictionId, setPredictionId] = useState(null);
  const [activePage, setActivePage] = useState("predict");
  const [preparing, setPreparing] = useState(false);
  const [operation, setOperation] = useState("");
  const [appAlert, setAppAlert] = useState({ message: "", type: "info" });
  const [busy, setBusy] = useState(false);
  const audioRequestRef = useRef(false);
  const pageRequestRef = useRef(0);
  const captureProfile = new URLSearchParams(window.location.search).get("capture") === "native" ? "native" : "legacy";
  const comparisonMode = new URLSearchParams(window.location.search).get("model") === "compare";
  const workshopMode = new URLSearchParams(window.location.search).get("model") === "workshop";
  const audio = useAudioRecorder({ onStatus: setStatus, captureProfile });
  const scanner = useQrScanner({ onResult: handleQrResult, onStatus: setStatus });
  const audioUrl = useAudioUrl(audio.audioBlob instanceof Blob ? audio.audioBlob : predictionAudio);
  const closeAlert = useCallback(() => setAppAlert({ message: "", type: "info" }), []);

  function clearPrediction() {
    setPredictionId(null);
    setPredictionAudio(null);
    setInferenceResult(null);
    setExperimentalResult(null);
    setModelLabels(null);
  }

  function resetAll() {
    clearPrediction();
    audio.reset();
    scanner.reset();
    setQrResult("");
    setVin("");
    setLabel(false);
    setDescription("");
    setMilage(0);
    setLocation("Iran Khodro");
    setStatus("idle");
  }

  async function handleLogout() {
    if (!beginAudioRequest("logout")) return;
    audio.reset();
    await scanner.reset();
    resetAll();
    setActivePage("predict");
    closeAlert();
    localStorage.clear();
    sessionStorage.clear();
    logout();
    endAudioRequest();
  }

  function applyPreviousRecording(data) {
    setLabel(data.exists);
    if (data.exists) {
      setAppAlert({ message: "⚠️ QR already exists. You can record new voice or change label.", type: "warning" });
      setStatus("Duplicate URL");
      setDescription(data.description);
      setMilage(data.milage);
      setLocation(data.location);
    }
  }

  async function hanldeVinResult(value) {
    if (!value) return;
    try {
      const response = await api.post("/check-vin", { vin: value });
      applyPreviousRecording(response.data);
    } catch (error) {
      setStatus("Failed to check QR link.");
      setAppAlert({ message: "Error checking QR link, you can still proceed.", type: "error" });
    }
  }

  async function handleQrResult(value) {
    setQrResult(value);
    setVin(value);
    setStatus("Checking QR link...");
    try {
      const response = await api.post("/check-url", { url: value });
      applyPreviousRecording(response.data);
      if (!response.data.exists) setStatus("QR code scanned ✓");
    } catch (error) {
      setStatus("Failed to check QR link.");
      setAppAlert({ message: "Error checking QR link, you can still proceed.", type: "error" });
    }
  }

  function labelForm() {
    const form = new FormData();
    form.append("location", location);
    form.append("milage", milage);
    form.append("description", description || "no description");
    form.append("url", qrResult);
    form.append("vin", qrResult ? "" : vin);
    return form;
  }

  async function handleEditLabel() {
    if (!beginAudioRequest("edit")) return;
    try {
      const form = labelForm();
      form.set("vin", vin);
      await api.post("/edit_label", form);
      setAppAlert({ message: "Label Changed!", type: "success" });
      resetAll();
    } catch (error) {
      setStatus("Failed to change");
      setAppAlert({ message: error.response?.data?.error || "An error occurred", type: "error" });
    } finally {
      endAudioRequest();
    }
  }

  async function resolveAudio() {
    const blob = await audio.getAudio();
    if (!blob?.size) {
      setAppAlert({ message: "Please record or upload audio first.", type: "warning" });
      return null;
    }
    return blob;
  }

  function beginAudioRequest(kind) {
    // The ref also blocks a second tap before React renders disabled controls.
    if (audioRequestRef.current) return false;
    audioRequestRef.current = true;
    setBusy(true);
    setOperation(kind);
    return true;
  }

  function endAudioRequest() {
    audioRequestRef.current = false;
    setBusy(false);
    setOperation("");
  }

  async function handleCaptureClick() {
    if (audio.recording) {
      if (!beginAudioRequest("stop")) return;
      try { await audio.getAudio(); }
      catch { setAppAlert({ message: "Could not finish the recording. Please try again.", type: "error" }); }
      finally { endAudioRequest(); }
      return;
    }
    if (audioRequestRef.current || preparing || scanner.scanning || scanner.closing) return;
    clearPrediction();
    audio.reset();
    setPreparing(true);
    try { await audio.handleRecordClick(); }
    finally { setPreparing(false); }
  }

  function handleAudioUpload(event) {
    if (!event.target.files?.length || audioRequestRef.current) return;
    clearPrediction();
    audio.handleAudioUpload(event);
  }

  function handleClearUploadedAudio() {
    if (audioRequestRef.current) return;
    clearPrediction();
    audio.handleClearUploadedAudio();
  }

  async function switchPage(page) {
    const request = ++pageRequestRef.current;
    if (page !== activePage) {
      const cleanup = scanner.reset();
      if (scanner.scanning || scanner.closing) await cleanup;
    }
    if (request === pageRequestRef.current) setActivePage(page);
  }

  function handleTabKey(event) {
    if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
    event.preventDefault();
    const page = event.key === "Home" ? "predict" : event.key === "End" ? "submit" : activePage === "predict" ? "submit" : "predict";
    switchPage(page);
    document.getElementById(`${page}-tab`)?.focus();
  }

  async function handleRunInference() {
    if (!beginAudioRequest("predict")) return;
    clearPrediction();
    try {
      const blob = await resolveAudio();
      if (!blob) return;
      setStatus("predicting...");
      const form = new FormData();
      form.append("audio", blob, blob.name || "recording.webm");
      form.append("capture_profile", audio.uploadedAudio ? "upload" : captureProfile);
      const predictionEndpoint = workshopMode ? "/predict?model=workshop" : comparisonMode ? "/predict?compare=1" : "/predict";
      const response = await api.post(predictionEndpoint, form);
      if (workshopMode && (response.data.model_mode !== "workshop" || !response.data.model_sha256)) {
        throw new Error("Workshop v3 is unavailable. Please try again later.");
      }
      setInferenceResult(response.data.inference);
      setExperimentalResult(response.data.comparison_inference || response.data.experimental_inference || null);
      setModelLabels(response.data.model_labels || null);
      setPredictionAudio(blob);
      setPredictionId(response.data.prediction_saved ? response.data.prediction_id : null);
      setStatus("Prediction ready");
    } catch (error) {
      setStatus("Prediction failed. You can retry with the same audio.");
      setAppAlert({ message: error.response?.data?.error || (workshopMode ? "Workshop v3 is unavailable. Please try again later." : "Inference failed."), type: "error" });
    } finally {
      endAudioRequest();
    }
  }

  async function handleSaveCorrection(correctedLabel) {
    if (!predictionId || !beginAudioRequest("feedback")) return null;
    try {
      const response = await api.post("/predict?feedback=1", {
        prediction_id: predictionId, corrected_label: correctedLabel,
      });
      return response.data;
    } finally {
      endAudioRequest();
    }
  }

  async function handleSendData() {
    if (!beginAudioRequest("submit")) return;
    try {
      const blob = await resolveAudio();
      if (!blob) return;
      if (!qrResult && !vin) {
        setAppAlert({ message: "Please scan a QR or input VIN.", type: "warning" });
        return;
      }
      setStatus("Sending data...");
      const form = labelForm();
      form.append("audio", blob, "recording.webm");
      await api.post("/upload", form);
      setAppAlert({ message: "Sent successfully!", type: "success" });
      resetAll();
    } catch (error) {
      setStatus("Failed to change");
      setAppAlert({ message: error.response?.data?.error || error.message || "An error occurred", type: "error" });
    } finally {
      endAudioRequest();
    }
  }

  if (!token) return <Login />;

  return (
    <div className="voice-app">
      <header className="voice-header"><div className="voice-header-inner">
        <div className="voice-brand"><img className="voice-motorava-symbol" src={`${process.env.PUBLIC_URL}/branding/motorava-symbol.svg`} alt="MotorAva" width="790" height="530" /></div>
        <div className="voice-header-actions">
          <img className="voice-ipco-logo" src={`${process.env.PUBLIC_URL}/branding/ipco.svg`} alt="IPCO — Iran Khodro Powertrain Company" width="738" height="738" />
          <details className="voice-account"><summary className="voice-icon-button" aria-label="Account"><Icon name="user" /></summary><div className="voice-account-menu"><p>Operator account</p><button type="button" className="voice-text-button" onClick={handleLogout} disabled={busy || preparing}><Icon name="logout" />Log out</button></div></details>
        </div>
      </div></header>
      <nav className="voice-nav" role="tablist" aria-label="Workspace">
        <button id="predict-tab" type="button" role="tab" aria-controls="prediction-view" aria-selected={activePage === "predict"} tabIndex={activePage === "predict" ? 0 : -1} onKeyDown={handleTabKey} onClick={() => switchPage("predict")}><Icon name="sound" />Predict</button>
        <button id="submit-tab" type="button" role="tab" aria-controls="submission-view" aria-selected={activePage === "submit"} tabIndex={activePage === "submit" ? 0 : -1} onKeyDown={handleTabKey} onClick={() => switchPage("submit")}><Icon name="folder" />Submit data</button>
      </nav>
      <main className="voice-main">
        <div className="voice-intro"><h1>{activePage === "predict" ? "Engine prediction" : "Submit a recording"}</h1><p>{activePage === "predict" ? "Record an engine. Get a prediction." : "Add vehicle details to your captured audio."}</p></div>
        {comparisonMode && <div className="voice-comparison-note"><strong>Model comparison</strong><p>Predict shows both models using the same audio.</p><a href="/">Return to current model</a></div>}
        {workshopMode && <div className="voice-comparison-note"><strong>Workshop · Engine model v3</strong><p>Experimental v3 predictions with {captureProfile} capture.</p><a href="/">Return to the original model</a></div>}
        <CustomAlert {...appAlert} onClose={closeAlert} />
        <div className="voice-workspace">
          <fieldset className="voice-capture-fieldset" disabled={busy || preparing || scanner.scanning || scanner.closing}>
            <RecordingControls {...audio} {...{audioUrl, busy, preparing, handleCaptureClick, handleRunInference, handleAudioUpload, handleClearUploadedAudio}} predicted={!!inferenceResult} predictionMode={activePage === "predict"} handleRecordAgain={handleCaptureClick} />
          </fieldset>
          <div id="prediction-view" role="tabpanel" aria-labelledby="predict-tab" hidden={activePage !== "predict"}>
            <PredictionPanel {...{inferenceResult, experimentalResult, modelLabels, comparisonMode, workshopMode, predictionAudio, audioUrl, predictionId}} busy={operation === "predict"} feedbackSaving={operation === "feedback"} disabled={busy || preparing || audio.recording} onSaveCorrection={handleSaveCorrection} />
          </div>
          <div id="submission-view" role="tabpanel" aria-labelledby="submit-tab" hidden={activePage !== "submit"}>
            <section className="voice-panel voice-data" aria-labelledby="vehicle-heading"><div className="voice-panel-head"><h2 id="vehicle-heading">Vehicle details</h2><button type="button" className="voice-text-button" onClick={handleEditLabel} disabled={!label || busy || preparing || audio.recording}>Edit label</button></div>
              <form onSubmit={(event) => { event.preventDefault(); handleSendData(); }}><fieldset className="voice-form" disabled={busy || preparing || audio.recording || scanner.closing}>
                <QrControls {...scanner} disabled={busy || preparing || audio.recording || scanner.closing} />
                <VinInput {...{vin, setVin, hanldeVinResult}} />
                <VehicleFields {...{location, setLocation, milage, setMilage, description, setDescription}} />
                <p className="voice-form-status" role="status">{status && status !== "idle" ? status : "Scan a QR code or enter a VIN to submit."}</p>
                <button type="submit" className="voice-button" disabled={!audio.audioBlob || (!qrResult && !vin) || scanner.scanning}><Icon name="send" />{operation === "submit" ? "Submitting…" : "Submit data"}</button>
              </fieldset></form>
            </section>
          </div>
        </div>
        {activePage === "predict" && /unavailable|failed/i.test(status) && <p className="voice-form-status" role="status">{status}</p>}
      </main>
      <footer className="voice-footer">Iran Khodro Powertrain Company</footer>
    </div>
  );
}
